import React, { useState, useRef, useMemo } from 'react';
import { message, Select, Input } from 'antd';
import { Form } from '@ant-design/compatible';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import gql from 'graphql-tag';
import { capitalize } from 'lodash';

import { landlordStatus as landlordStatusConstant } from '~client/constants/landlord';

import Modal from 'common/components/modal';

import useCommonStyles from './details/style';

const MUTATION_UPDATE_LANDLORD_STATUS = gql`
  mutation updateLandlordStatus($landlordId: ID!, $notes: String, $status: LandlordStatus!) {
    updateLandlordStatus(landlordId: $landlordId, notes: $notes, status: $status) {
      landlord {
        id
      }
    }
  }
`;

const useStyles = makeStyles(
  theme => ({
    textBtn: {
      border: 0,
      boxShadow: 'none',
      fontSize: 14,
      lineHeight: '22px',
      color: '#38b2a6',
      display: 'flex',
      alignItems: 'center',
      fontWeight: 600,
      '&:hover': {
        cursor: 'pointer',
      },
      '&::before': {
        content: '""',
        display: 'inline-block',
        height: 16,
        width: 1,
        backgroundColor: '#d9d9d9',
        marginRight: 16,
      },
    },
    notice: {
      padding: '9px 12px',
      backgroundColor: theme.colors.gray2,
      marginBottom: 16,
    },
    noticeText: {
      ...theme.typography.body4,
      color: theme.colors.bodyColor,
    },
    noticeStrongText: {
      ...theme.typography.body4,
      color: theme.colors.bodyColor,
      fontWeight: '600',
    },
  }),
  { name: 'verifyLandlord' },
);
const formatStatusStr = str =>
  str
    ?.split('_')
    ?.map(item => capitalize(item))
    ?.join(' ');

const LandlordBtn = ({ form, landlordId, landlordStatus, successCallBack }) => {
  const { getFieldDecorator, validateFields } = form;

  const { t } = useTranslation();
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const ref = useRef();

  const [visible, setVisible] = useState(false);

  const [updateLandlordStatus] = useMutation(MUTATION_UPDATE_LANDLORD_STATUS, {
    onCompleted: () => {
      successCallBack();
      setVisible(false);
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      }
    },
  });

  const landlordStatusOpts = useMemo(() => {
    const { PENDING, VERIFIED, IN_CONSULTATION, REJECTED, LOST, UNPUBLISHED } =
      landlordStatusConstant;

    switch (landlordStatus) {
      case PENDING:
        return [IN_CONSULTATION, VERIFIED, REJECTED, LOST];
      case IN_CONSULTATION:
        return [VERIFIED, REJECTED, LOST];
      case VERIFIED:
        return [UNPUBLISHED];
      case LOST:
      case REJECTED:
        return [IN_CONSULTATION, VERIFIED];
      case UNPUBLISHED:
        return [VERIFIED];
      default:
        return [];
    }
  }, []);

  const handleCancelClick = () => {
    setVisible(false);
  };

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { notes, status } = values;

        updateLandlordStatus({
          variables: {
            notes,
            status,
            landlordId,
          },
        });
      }
    });
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className={classes.textBtn} onClick={() => setVisible(true)}>
        {t('cms.landlord.button.update_status')}
      </div>

      <Modal
        title={t('cms.landlord.landlord_status.modal.title')}
        width={446}
        visible={visible}
        wrapClassName={commonClasses.modalWrapper}
        okText={t('cms.landlord.modal.confirm.button')}
        cancelButtonProps={{
          className: 'ant-btn-cancel',
        }}
        okButtonProps={{
          className: 'ant-btn-confirm',
        }}
        onCancel={handleCancelClick}
        onOk={handleConfirmClick}
      >
        <div className={classes.notice}>
          <span className={classes.noticeText}>{t('cms.landlord.landlord_status.subtitle')}: </span>

          <span className={classes.noticeStrongText}>{formatStatusStr(landlordStatus)}</span>
        </div>
        <Form className={commonClasses.formWrapper}>
          <Form.Item label={t('cms.landlord.landlord_status.update_status_to.label')}>
            {getFieldDecorator('status', {
              rules: [
                {
                  required: true,
                  message: t('cms.landlord.landlord_status.update_status_to.error'),
                },
              ],
            })(
              <Select placeholder={t('cms.landlord.landlord_status.update_status_to.placeholder')}>
                <For of={landlordStatusOpts} each="statusOpt">
                  <Select.Option value={statusOpt}>{formatStatusStr(statusOpt)}</Select.Option>
                </For>
              </Select>,
            )}
          </Form.Item>
          <Form.Item label={t('cms.landlord.landlord_status.note.label')}>
            {getFieldDecorator('notes', {
              rules: [
                {
                  max: 300,
                  type: 'string',
                  message: t('cms.landlord.landlord_status.note.error'),
                },
              ],
            })(
              <Input.TextArea
                placeholder={t('cms.landlord.landlord_status.note.placeholder')}
                rows={3}
              />,
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const UpdateStatusLandlordBtn = Form.create({
  name: 'update_landlord_status',
})(LandlordBtn);

export default UpdateStatusLandlordBtn;
